import { AndroidBiometryStrength, BiometricAuth } from "@aparajita/capacitor-biometric-auth";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";
import { isNative } from "../utils/platform";

const PIN_KEY = "tripai_local_pin_v1";
const METHOD_KEY = "tripai_local_lock_method_v1";
const ENABLED_KEY = "tripai_local_lock_enabled_v1";
const TIMEOUT_KEY = "tripai_local_lock_timeout_v1";

export const LOCAL_LOCK_SETTINGS_EVENT = "tripai:local-lock-settings-changed";

export type LocalLockMethod = "biometric" | "password" | "pin";
export type LocalLockTimeout = 0 | 1 | 5 | 15;

export type LocalLockSettings = {
  enabled: boolean;
  method: LocalLockMethod;
  timeoutMinutes: LocalLockTimeout;
  hasPin: boolean;
};

export type BiometricAuthResult = {
  ok: boolean;
  code?: string;
  message?: string;
};

let biometricPromptInFlight = false;

function notifySettingsChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(LOCAL_LOCK_SETTINGS_EVENT));
}

async function readValue(key: string): Promise<string | null> {
  if (!isNative()) return localStorage.getItem(key);
  try {
    const result = await SecureStoragePlugin.get({ key });
    return result.value ?? null;
  } catch {
    return null;
  }
}

async function writeValue(key: string, value: string): Promise<void> {
  if (!isNative()) {
    localStorage.setItem(key, value);
    return;
  }
  await SecureStoragePlugin.set({ key, value });
}

async function removeValue(key: string): Promise<void> {
  if (!isNative()) {
    localStorage.removeItem(key);
    return;
  }
  try {
    await SecureStoragePlugin.remove({ key });
  } catch {
    // Missing secure-storage keys are already effectively removed.
  }
}

async function hashPin(pin: string): Promise<string> {
  const bytes = new TextEncoder().encode(pin);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

function normalizeMethod(value: string | null): LocalLockMethod {
  return value === "pin" || value === "password" || value === "biometric" ? value : "biometric";
}

function normalizeTimeout(value: string | null): LocalLockTimeout {
  // Missing preference must use the one-minute default, not Number(null) === 0 (immediate).
  if (value === null || !value.trim()) return 1;
  const parsed = Number(value);
  return parsed === 0 || parsed === 5 || parsed === 15 ? parsed : 1;
}

export type BiometricMessageKey =
  | "security.biometricNativeOnly"
  | "security.biometricNotEnrolled"
  | "security.biometricUnavailable"
  | "security.deviceCredentialMissing"
  | "security.biometricLockout"
  | "security.biometricFailed"
  | "security.biometricSystemCancel"
  | "security.biometricCancelled"
  | "security.biometricTimeout"
  | "security.biometricChecking";

export function biometricMessageKey(result: BiometricAuthResult): BiometricMessageKey {
  switch (result.code) {
    case "nativeOnly": return "security.biometricNativeOnly";
    case "biometryNotEnrolled": return "security.biometricNotEnrolled";
    case "biometryNotAvailable": return "security.biometricUnavailable";
    case "passcodeNotSet":
    case "noDeviceCredential": return "security.deviceCredentialMissing";
    case "biometryLockout": return "security.biometricLockout";
    case "authenticationFailed": return "security.biometricFailed";
    case "systemCancel": return "security.biometricSystemCancel";
    case "userCancel":
    case "userFallback":
    case "appCancel": return "security.biometricCancelled";
    case "biometricPromptTimeout":
    case "biometricCheckTimeout": return "security.biometricTimeout";
    case "promptInFlight": return "security.biometricChecking";
    default: return "security.biometricUnavailable";
  }
}

function errorInfo(error: unknown): { code: string; message: string } {
  if (!error || typeof error !== "object") {
    return { code: "unknown", message: String(error || "") };
  }
  const candidate = error as { code?: unknown; message?: unknown };
  return {
    code: String(candidate.code || "unknown"),
    message: String(candidate.message || "")
  };
}

function withTimeout<T>(promise: Promise<T>, milliseconds: number, timeoutCode: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let completed = false;
    const timer = window.setTimeout(() => {
      if (completed) return;
      completed = true;
      const error = new Error(timeoutCode) as Error & { code?: string };
      error.code = timeoutCode;
      reject(error);
    }, milliseconds);

    promise.then((value) => {
      if (completed) return;
      completed = true;
      window.clearTimeout(timer);
      resolve(value);
    }).catch((error) => {
      if (completed) return;
      completed = true;
      window.clearTimeout(timer);
      reject(error);
    });
  });
}

export async function checkBiometricAvailability(): Promise<BiometricAuthResult> {
  if (!isNative()) {
    return { ok: false, code: "nativeOnly", message: "Biometrics are available only in the installed Android or iOS app." };
  }

  try {
    const result: any = await withTimeout(BiometricAuth.checkBiometry() as Promise<any>, 8000, "biometricCheckTimeout");
    if (result.isAvailable || result.deviceIsSecure) return { ok: true };
    return {
      ok: false,
      code: String(result.code || result.strongCode || "biometryNotAvailable"),
      message: String(result.reason || result.strongReason || "")
    };
  } catch (error) {
    const info = errorInfo(error);
    return { ok: false, code: info.code, message: info.message };
  }
}

export async function biometricAvailable(): Promise<boolean> {
  return (await checkBiometricAvailability()).ok;
}

export async function authenticateBiometricDetailed(reason: string): Promise<BiometricAuthResult> {
  if (biometricPromptInFlight) return { ok: false, code: "promptInFlight" };
  biometricPromptInFlight = true;

  try {
    const availability = await checkBiometricAvailability();
    if (!availability.ok) return availability;

    await withTimeout(BiometricAuth.authenticate({
      reason: reason || "Unlock Mundely",
      cancelTitle: "Annulla",
      allowDeviceCredential: true,
      iosFallbackTitle: "Usa codice",
      androidTitle: "Mundely",
      androidConfirmationRequired: false,
      androidBiometryStrength: AndroidBiometryStrength.weak
    }), 30000, "biometricPromptTimeout");

    return { ok: true };
  } catch (error) {
    const info = errorInfo(error);
    return { ok: false, code: info.code, message: info.message };
  } finally {
    biometricPromptInFlight = false;
  }
}

export async function authenticateBiometric(reason: string): Promise<boolean> {
  return (await authenticateBiometricDetailed(reason)).ok;
}

export function isBiometricPromptInFlight(): boolean {
  return biometricPromptInFlight;
}

export async function savePin(pin: string): Promise<void> {
  if (!/^\d{4}$/.test(pin)) throw new Error("INVALID_PIN");
  await writeValue(PIN_KEY, await hashPin(pin));
  notifySettingsChanged();
}

export async function hasConfiguredPin(): Promise<boolean> {
  return Boolean(await readValue(PIN_KEY));
}

export async function verifyPin(pin: string): Promise<boolean> {
  if (!/^\d{4}$/.test(pin)) return false;
  const stored = await readValue(PIN_KEY);
  if (!stored) return false;
  return stored === await hashPin(pin);
}

export async function setLocalLockMethod(method: LocalLockMethod): Promise<void> {
  await writeValue(METHOD_KEY, method);
  notifySettingsChanged();
}

export async function setBiometricMethod(): Promise<void> {
  await setLocalLockMethod("biometric");
}

export async function setLocalLockEnabled(enabled: boolean): Promise<void> {
  await writeValue(ENABLED_KEY, enabled ? "1" : "0");
  notifySettingsChanged();
}

export async function setLocalLockTimeout(timeoutMinutes: LocalLockTimeout): Promise<void> {
  await writeValue(TIMEOUT_KEY, String(timeoutMinutes));
  notifySettingsChanged();
}

export async function getLocalLockMethod(): Promise<LocalLockMethod | null> {
  const raw = await readValue(METHOD_KEY);
  if (raw === "pin" || raw === "password" || raw === "biometric") return raw;
  return null;
}

export async function getLocalLockSettings(): Promise<LocalLockSettings> {
  const [methodRaw, enabledRaw, timeoutRaw, pinRaw] = await Promise.all([
    readValue(METHOD_KEY),
    readValue(ENABLED_KEY),
    readValue(TIMEOUT_KEY),
    readValue(PIN_KEY)
  ]);

  // Migration from Mundely's first implementation: the presence of a saved
  // method meant that local protection was active because no separate switch existed.
  const enabled = enabledRaw === null ? Boolean(methodRaw) : enabledRaw === "1";

  return {
    enabled,
    method: normalizeMethod(methodRaw),
    timeoutMinutes: normalizeTimeout(timeoutRaw),
    hasPin: Boolean(pinRaw)
  };
}

export async function clearLocalLock(): Promise<void> {
  await Promise.all([
    removeValue(PIN_KEY),
    removeValue(METHOD_KEY),
    removeValue(ENABLED_KEY),
    removeValue(TIMEOUT_KEY)
  ]);
  notifySettingsChanged();
}
